import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Download, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { openCookieSettings } from "@/lib/consent";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

export function PrivacySection() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/users/me/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "mis-datos-autopresupuestos.json" });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Datos descargados");
    } catch { toast.error("No se pudieron exportar los datos"); }
    finally { setExporting(false); }
  };

  return (
    <div className="mt-px border border-slate-200 bg-white p-6" data-testid="privacy-section">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Privacidad y datos personales</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Gestiona tus derechos RGPD. Consulta la <Link to="/politica-de-privacidad" className="text-orange-600 underline">Política de Privacidad</Link>.
      </p>
      <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 p-3 mb-5" data-testid="privacy-consent-info">
        {user?.consent_date
          ? <>Aceptaste la Política de Privacidad (v{user.consent_version}) el {new Date(user.consent_date).toLocaleString("es-ES")}.</>
          : <>No consta un registro de consentimiento para esta cuenta.</>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={exportData} disabled={exporting} data-testid="privacy-export-btn"
          className="h-11 px-4 border border-slate-200 hover:border-slate-900 text-sm font-bold inline-flex items-center gap-2 transition-colors">
          <Download className="w-4 h-4" /> {exporting ? "Preparando…" : "Descargar mis datos (JSON)"}
        </button>
        <button onClick={openCookieSettings} data-testid="privacy-cookie-settings-btn"
          className="h-11 px-4 border border-slate-200 hover:border-slate-900 text-sm font-bold transition-colors">
          Configurar cookies
        </button>
      </div>
      {user?.role !== "admin" && <DeleteAccount />}
    </div>
  );
}

function DeleteAccount() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me", { data: { password, confirm } });
      const bytes = Uint8Array.from(atob(data.certificate_pdf), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      Object.assign(document.createElement("a"), { href: url, download: `${data.certificate_id}.pdf` }).click();
      toast.success(`Cuenta eliminada · certificado ${data.certificate_id} descargado${data.email_sent ? " y enviado por email" : ""}`);
      setTimeout(logout, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la cuenta");
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-red-100">
      <div className="font-bold text-sm text-red-700">Zona de peligro</div>
      <p className="text-xs text-slate-500 mt-1 mb-3">Se borrarán de forma permanente tu cuenta, clientes, presupuestos, gastos y archivos. Se cancelará tu suscripción.</p>
      <button onClick={() => setOpen(true)} data-testid="delete-account-btn"
        className="h-11 px-4 border border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 text-sm font-bold inline-flex items-center gap-2 transition-colors">
        <Trash2 className="w-4 h-4" /> Eliminar cuenta
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-none" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-extrabold text-2xl">¿Eliminar tu cuenta definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Por obligación fiscal, las <strong>facturas emitidas y sus registros Verifactu</strong> se
              desvincularán de tu cuenta y quedarán bloqueados durante el plazo legal (hasta 6 años). Recibirás un certificado de borrado en PDF (descarga + email). Descarga antes tus datos si los necesitas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Tu contraseña"
              data-testid="delete-account-password" className="w-full h-12 px-3 border border-slate-200 outline-none focus:border-red-600" />
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Escribe ELIMINAR para confirmar"
              data-testid="delete-account-confirm" className="w-full h-12 px-3 border border-slate-200 outline-none focus:border-red-600 font-mono" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none h-11" data-testid="delete-account-cancel">Cancelar</AlertDialogCancel>
            <button onClick={doDelete} disabled={busy || !password || confirm.trim().toUpperCase() !== "ELIMINAR"} data-testid="delete-account-submit"
              className="h-11 px-5 bg-red-600 text-white font-bold text-sm hover:bg-red-700 disabled:opacity-40 transition-colors">
              {busy ? "Eliminando…" : "Eliminar definitivamente"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
